<script setup lang="ts">
import { computed, type PropType } from 'vue';
import type { ContractManagementDateInDb } from '@/api';
import {
  DATE_TYPE_LABELS,
  RECURRENCE_FREQUENCY_LABELS,
} from '@/services/contractManagement/dateRow';
import { staticEntityName } from '@/utils/contractManagementView/staticEntityName';
import GaiaAddButton from '../../base/GaiaAddButton.vue';
import GaiaDeleteButton from '../../base/GaiaDeleteButton.vue';
import GaiaDropdown from '../../base/GaiaDropdown.vue';
import GaiaMenuSurface from '../../base/GaiaMenuSurface.vue';
import GaiaPopover from '../../base/GaiaPopover.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaPopconfirm from '../../composite/GaiaPopconfirm.vue';
import { gaiaNeutral } from '../../themes/tokens';

const props = defineProps({
  /** The read version's dates. */
  dates: {
    type: Array as PropType<ContractManagementDateInDb[]>,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** Why the caller can't change the dates; null when they can. */
  writeBlock: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{
  edit: [date: ContractManagementDateInDb];
  add: [type: string];
  delete: [date: ContractManagementDateInDb];
}>();

/** Known date types in reading order — the term starts before it ends. */
const TYPE_ORDER: string[] = Object.keys(DATE_TYPE_LABELS);

function rank(type: string): number {
  const index = TYPE_ORDER.indexOf(type);
  return index === -1 ? TYPE_ORDER.length : index;
}

/** Known types first, in order, then any other; a type's dates by date. */
const sorted = computed(() =>
  [...props.dates].sort(
    (a, b) => rank(a.type) - rank(b.type) || (a.date ?? '').localeCompare(b.date ?? '')
  )
);

function labelOf(date: ContractManagementDateInDb): string {
  return DATE_TYPE_LABELS[date.type] ?? date.type;
}

/** "every year", "every 2 months"; null for a date that doesn't repeat. */
function recurrenceOf(date: ContractManagementDateInDb): string | null {
  if (date.recurrence_type === 'never' || !date.recurrence_frequency) return null;
  const unit = RECURRENCE_FREQUENCY_LABELS[date.recurrence_frequency].toLowerCase();
  const interval = date.recurrence_interval ?? 1;
  return interval > 1 ? `every ${interval} ${unit}s` : `every ${unit}`;
}

/** Any known type can be added, also one the version already has a date of. */
const addOptions = TYPE_ORDER.map(type => ({ label: DATE_TYPE_LABELS[type], key: type }));

function edit(date: ContractManagementDateInDb): void {
  if (!props.writeBlock) emit('edit', date);
}

const chipBorder = gaiaNeutral.border;
const chipHover = gaiaNeutral.surfaceHover;
</script>

<template>
  <!-- The file's key dates, one chip each (type, date, how it repeats): the
       description under the pointer, a click to edit, ✕ to delete, and a way to
       add another date of any type. -->
  <n-flex align="center" :size="6">
    <gaia-text v-if="props.loading" size="dense" tone="faint">Loading…</gaia-text>
    <template v-else>
      <span v-for="date in sorted" :key="date.uid" class="chip">
        <gaia-popover
          :disabled="!date.description"
          trigger="hover"
          placement="bottom-start"
          :delay="200"
        >
          <template #trigger>
            <button
              type="button"
              class="chip-body"
              :class="{ editable: !props.writeBlock }"
              :title="props.writeBlock ?? `Edit ${labelOf(date).toLowerCase()}`"
              @click="edit(date)"
            >
              <gaia-text size="micro" tone="subtle">{{ labelOf(date) }}</gaia-text>
              <gaia-text size="dense" weight="semibold" :tone="date.date ? 'ink' : 'muted'">
                {{ date.date ? staticEntityName(date) : 'Not set' }}
              </gaia-text>
              <gaia-text v-if="recurrenceOf(date)" size="micro" tone="subtle">
                · {{ recurrenceOf(date) }}
              </gaia-text>
            </button>
          </template>
          <gaia-menu-surface width="320px">
            <n-flex vertical :size="6" :style="{ padding: '6px 8px' }">
              <gaia-text variant="label" size="nano" tone="subtle">Description</gaia-text>
              <gaia-text size="row" tone="text" :style="{ lineHeight: 1.5 }">
                {{ date.description }}
              </gaia-text>
            </n-flex>
          </gaia-menu-surface>
        </gaia-popover>
        <gaia-popconfirm title="Delete this date?" @confirm="emit('delete', date)">
          <template #trigger>
            <gaia-delete-button
              size="xs"
              subject="date"
              :disabled="Boolean(props.writeBlock)"
              :title="props.writeBlock ?? undefined"
            />
          </template>
        </gaia-popconfirm>
      </span>
      <gaia-dropdown
        v-if="!props.writeBlock"
        :options="addOptions"
        @select="(type: string) => emit('add', type)"
      >
        <gaia-add-button size="xs" variant="dashed" subject="date" />
      </gaia-dropdown>
      <gaia-text v-if="sorted.length === 0 && props.writeBlock" size="dense" tone="subtle">
        No dates
      </gaia-text>
    </template>
  </n-flex>
</template>

<style scoped>
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 4px 0 10px;
  border: 1px solid v-bind(chipBorder);
  border-radius: 8px;
  white-space: nowrap;
}

.chip:hover {
  background: v-bind(chipHover);
}

.chip-body {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  cursor: default;
}

.chip-body.editable {
  cursor: pointer;
}
</style>
